import { toConsumableArray as _toConsumableArray, typeof as _typeof, slicedToArray as _slicedToArray } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useCallback, useState, useEffect } from 'react';
import { useSafeThemeUI } from '../theme-provider/use-theme-ui.esm.js';
import { defaultBreakpoints } from '../themes/theme-keys/breakpoints.esm.js';
import { isBrowser } from '../utils/dom.esm.js';
import executeWhenChangeStop from '../utils/execute-when-change-stop.esm.js';
import { memoizedGet } from '../utils/object.esm.js';

var useBreakpointIndex = function useBreakpointIndex() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var theme = useSafeThemeUI();
  var _options$defaultIndex = options.defaultIndex,
    defaultIndex = _options$defaultIndex === void 0 ? 0 : _options$defaultIndex,
    _options$updateOnResi = options.updateOnResizeChangeStopPeriod,
    updateOnResizeChangeStopPeriod = _options$updateOnResi === void 0 ? 0 : _options$updateOnResi;
  var breakpoints = memoizedGet(theme, 'breakpoints', defaultBreakpoints);
  var getIndex = useCallback(function () {
    if (!isBrowser) {
      if (typeof defaultIndex === 'number') {
        if (defaultIndex < 0 || defaultIndex > breakpoints.keys.length - 1) {
          throw new RangeError("Default breakpoint index out of range. Theme has ".concat(breakpoints.keys.length, " breakpoints, got index ").concat(defaultIndex));
        }
        return defaultIndex;
      }
      throw new TypeError("Default breakpoint index should be a number. Got: ".concat(defaultIndex, ", ").concat(_typeof(defaultIndex)));
    }
    return breakpoints.keys.filter(function (key) {
      if (process.env.NODE_ENV !== 'test') {
        return breakpoints.getValueAndUnit(key) && window.matchMedia("screen and (min-width: ".concat(breakpoints.getValueAndUnit(key), ")")).matches;
      }
      return breakpoints.getValueAndUnit(key);
    }).length;
  }, [breakpoints, defaultIndex]);
  var _useState = useState(getIndex),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  useEffect(function () {
    var _executeWhenChangeSto = executeWhenChangeStop(function () {
        var newValue = getIndex();
        if (value !== newValue) {
          setValue(newValue);
        }
      }, updateOnResizeChangeStopPeriod),
      _executeWhenChangeSto2 = _slicedToArray(_executeWhenChangeSto, 2),
      onResize = _executeWhenChangeSto2[0],
      reset = _executeWhenChangeSto2[1];
    if (process.env.NODE_ENV !== 'test') {
      isBrowser && window.addEventListener('resize', onResize);
    }
    return function () {
      reset();
      if (process.env.NODE_ENV !== 'test') {
        isBrowser && window.removeEventListener('resize', onResize);
      }
    };
  }, [breakpoints, getIndex, value, updateOnResizeChangeStopPeriod]);
  return value;
};
function useResponsiveValue(values) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var theme = useSafeThemeUI();
  var array = typeof values === 'function' ? values(theme) : values;
  var safeArray = array.reduce(function (acc, cur, indx) {
    var next = (cur === null || cur === undefined) && indx > 0 ? array[indx - 1] : cur;
    return [].concat(_toConsumableArray(acc), [next]);
  }, []);
  var index = useBreakpointIndex(options);
  return safeArray[index >= safeArray.length ? safeArray.length - 1 : index];
}

export default useResponsiveValue;
export { useBreakpointIndex };
//# sourceMappingURL=use-responsive-value.esm.js.map
